import * as Sentry from "@sentry/react";
import { isRtdbPermissionDenied } from "./rtdbErrors.js";

// Codes and names that are expected user outcomes. They still surface in the
// UI through LaunchError, but they are not application defects.
const EXPECTED_USER_ERROR_CODES = new Set([
  "auth/invalid-email",
  "auth/invalid-password",
  "auth/wrong-password",
  "auth/invalid-credential",
  "auth/user-not-found",
  "auth/email-already-exists",
  "auth/invalid-verification-code",
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/network-request-failed",
  "authWrongPassword",
  "authUserNotFound",
  "authEmailAlreadyExists",
  "authInvalidPassword",
  "authInvalidVerificationCode",
  "passwordInvalid",
  "passwordMustBe6Characters",
  "ERR_NETWORK",
  "ERR_CANCELED",
  "ECONNABORTED",
  "errorNetworkConnection",
  "notHaveEnoughUsageValue",
  "notHaveEnoughBrandUsageValue",
  "integrationInformationNotFound",
  "integrationMissingPermission",
  "LEADGEN_TOS_NOT_ACCEPTED",
  "campaignRequestAborted",
  "oauthInWindowNoCompleted",
  "card_declined",
  "expired_card",
  "incorrect_cvc",
  "incorrect_number",
  "generic_decline",
  "insufficient_funds",
  "lost_card",
  "stolen_card",
  "authCardDeclined",
  "authExpiredCard",
  "authIncorrectCvc",
  "authIncorrectNumber",
  "authGenericDecline",
  "authInsufficientFunds",
  "authLostCard",
  "authStolenCard",
  "authIntentAuthFailure",
  "freeBotMessagesLimitReached",
  "textRegenerationLimitReached",
  "campaignIsGenerating",
  "customReportNoActiveCampaignsInPeriod",
  "DUPLICATE_CODE",
  "INVALID_CODE",
  "cancelled",
  "canceled",
  "permission-denied",
  "PERMISSION_DENIED",
  "whiteLabelNotEligible",
  "whiteLabelAlreadyActive",
  "whiteLabelNotPurchased",
  "whiteLabelInvalidBranding",
  "whiteLabelInvalidTransition",
  "accountRoleInvalid",
]);

const CANCELLED_MESSAGES = [
  /^produce cancelled$/i,
  /^the operation was aborted$/i,
  /^user aborted a request$/i,
  /^aborted$/i,
  /^cancelled$/i,
  /^canceled$/i,
];

let sentryOverride = null;

export const setSentryClientForTests = (client) => {
  sentryOverride = client;
};

const getSentry = () => sentryOverride || Sentry;

const hasHttpDsn = (dsn) =>
  typeof dsn === "string" &&
  (dsn.trim().startsWith("https://") || dsn.trim().startsWith("http://"));

export const resolveSentryEnabled = ({
  sentryDsn,
  isStaging,
  isDev = false,
} = {}) => {
  const dsn = typeof sentryDsn === "string" ? sentryDsn.trim() : "";
  const staging = String(isStaging ?? "")
    .trim()
    .toLowerCase();
  const hasDsn = hasHttpDsn(dsn);
  const isProduction = staging === "false";

  return {
    enabled: hasDsn && isProduction && isDev !== true,
    dsn: hasDsn ? dsn : "",
    // Production builds with no DSN drop every LaunchError on the floor.
    misconfigured: isProduction && isDev !== true && !hasDsn,
    environment: isProduction ? "production" : "staging",
  };
};

const REDACTED = "[Filtered]";
const MAX_SCRUB_DEPTH = 8;

const SENSITIVE_KEYS = new Set([
  "authorization",
  "proxyauthorization",
  "cookie",
  "setcookie",
  "password",
  "passwd",
  "secret",
  "token",
  "idtoken",
  "accesstoken",
  "refreshtoken",
  "apikey",
  "privatekey",
  "clientsecret",
  "cvv",
  "cvc",
  "cardnumber",
  "pan",
  "phone",
  "telephone",
  "mobile",
  "address",
  "bearer",
  "credential",
  "credentials",
  "otp",
  "otpcode",
  "licensekey",
  "customtoken",
  "card",
  "last4",
]);

const BODY_KEYS = new Set([
  "body",
  "payload",
  "requestbody",
  "responsebody",
  "requestdata",
  "responsedata",
]);

const normalizeKey = (key) =>
  String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const isSensitiveKey = (key) => {
  const normalized = normalizeKey(key);
  if (!normalized) return false;
  if (normalized.startsWith("has") || normalized.startsWith("is")) return false;
  if (SENSITIVE_KEYS.has(normalized)) return true;
  return (
    normalized.endsWith("password") ||
    normalized.endsWith("secret") ||
    normalized.endsWith("token") ||
    normalized.endsWith("apikey") ||
    normalized.endsWith("phone")
  );
};

export const scrubString = (value) => {
  if (typeof value !== "string" || !value) return value;
  return value
    .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, "Bearer [Filtered]")
    .replace(/\bsk-[A-Za-z0-9_-]{8,}/g, "[Filtered]")
    .replace(/\bEAA[A-Za-z0-9]{20,}/g, "[Filtered]")
    .replace(
      /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{8,}/g,
      "[Filtered]"
    )
    .replace(/\bya29\.[A-Za-z0-9\-._~+/]+=*/g, "[Filtered]")
    .replace(
      /(^|[?&#])((?:client_secret|auth_code|oob_?code|access[_-]?token|refresh[_-]?token|id[_-]?token|api[_-]?key|password|signature|secret|token|phone|code|state|key))=[^&#\s]*/gi,
      "$1$2=[Filtered]"
    );
};

export const describeValueShape = (value) => {
  if (value == null) return { type: "empty" };
  if (Array.isArray(value)) return { type: "array", size: value.length };
  if (typeof value === "string") return { type: "string", size: value.length };
  if (typeof value !== "object") return { type: typeof value };
  const keys = Object.keys(value);
  return {
    type: "object",
    size: keys.length,
    keys: keys.slice(0, 40).map((key) => scrubString(String(key))),
  };
};

const isAxiosConfig = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      value.headers &&
      typeof value.headers === "object" &&
      (value.url || value.method || value.baseURL)
  );

const isAxiosErrorLike = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      (value.isAxiosError === true ||
        (value.config &&
          (value.response || value.request || isAxiosConfig(value.config))))
  );

const scrubAxiosConfig = (value) => ({
  method: value?.method || null,
  url: scrubString(typeof value?.url === "string" ? value.url : ""),
  dataShape: describeValueShape(value?.data),
});

const scrubAxiosError = (value) => ({
  message: scrubString(typeof value?.message === "string" ? value.message : ""),
  code:
    typeof value?.code === "string"
      ? scrubString(value.code)
      : (value?.code ?? null),
  status: value?.response?.status ?? value?.status ?? null,
  requestShape: describeValueShape(value?.config?.data),
  responseShape: describeValueShape(value?.response?.data),
});

const sanitizeErrorForCapture = (rawError) => {
  if (!(rawError instanceof Error)) return rawError;
  const message = scrubString(rawError.message || "");
  const stack = typeof rawError.stack === "string" ? rawError.stack : "";
  const scrubbedStack = stack ? scrubString(stack) : stack;
  if (
    !isAxiosErrorLike(rawError) &&
    message === rawError.message &&
    scrubbedStack === stack
  ) {
    return rawError;
  }
  const safeError = new Error(message);
  safeError.name = rawError.name;
  if (scrubbedStack) safeError.stack = scrubbedStack;
  if (rawError.code !== undefined) safeError.code = rawError.code;
  return safeError;
};

const ERROR_CLASS_NAME = /^[A-Z][A-Za-z0-9]*Error$/;

const isPlainErrorRecord = (value) =>
  typeof value?.message === "string" || typeof value?.stack === "string";

export const scrubSentryValue = (value, depth = 0, seen = new WeakSet()) => {
  if (value == null) return value;
  if (typeof value === "string") return scrubString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "bigint") return String(value);
  if (typeof value === "function") return "[Function]";
  if (depth > MAX_SCRUB_DEPTH) return "[MaxDepth]";
  if (typeof value !== "object") return scrubString(String(value));
  // Only objects still on the walk stack are cycles. Shared refs serialize.
  if (seen.has(value)) return "[Circular]";
  seen.add(value);

  try {
    if (isAxiosErrorLike(value)) return scrubAxiosError(value);
    if (value instanceof Error) {
      return {
        name: value.name,
        message: scrubString(value.message || ""),
        code: value.code ?? null,
      };
    }
    if (Array.isArray(value)) {
      return value.map((item) => scrubSentryValue(item, depth + 1, seen));
    }
    if (isAxiosConfig(value)) return scrubAxiosConfig(value);

    const output = {};
    const looksLikeHttp =
      Object.prototype.hasOwnProperty.call(value, "headers") ||
      Object.prototype.hasOwnProperty.call(value, "config");

    Object.entries(value).forEach(([key, child]) => {
      const normalized = normalizeKey(key);
      if (
        normalized === "headers" ||
        normalized === "cookie" ||
        normalized === "setcookie"
      ) {
        return;
      }
      if (normalized === "config" && child && typeof child === "object") {
        output.config = isAxiosConfig(child)
          ? scrubAxiosConfig(child)
          : scrubSentryValue(child, depth + 1, seen);
        return;
      }
      if (normalized === "request" && looksLikeHttp) return;
      if (
        normalized === "name" &&
        typeof child === "string" &&
        ERROR_CLASS_NAME.test(child) &&
        isPlainErrorRecord(value)
      ) {
        output[key] = scrubString(child);
        return;
      }
      if (isSensitiveKey(key)) {
        output[key] = REDACTED;
        return;
      }
      if (
        BODY_KEYS.has(normalized) ||
        (normalized === "data" &&
          (looksLikeHttp ||
            Object.prototype.hasOwnProperty.call(value, "status")))
      ) {
        output[key] =
          child && typeof child === "object"
            ? describeValueShape(child)
            : scrubSentryValue(child, depth + 1, seen);
        return;
      }
      output[key] = scrubSentryValue(child, depth + 1, seen);
    });

    return output;
  } finally {
    seen.delete(value);
  }
};

export const scrubSentryUser = (user) => {
  if (!user || typeof user !== "object") return undefined;
  const userID = user.userID || user.id || user.uid;
  if (userID === undefined || userID === null || userID === "")
    return undefined;
  const id = String(userID);
  if (id.includes("@") || /\bBearer\s+/i.test(id) || id.startsWith("eyJ")) {
    return undefined;
  }
  return { id };
};

const readUserAgent = (headers) => {
  if (!headers || typeof headers !== "object") return undefined;
  const entry = Object.entries(headers).find(
    ([key]) => normalizeKey(key) === "useragent"
  );
  if (!entry || typeof entry[1] !== "string" || !entry[1]) return undefined;
  return scrubString(entry[1]);
};

const SPAN_URL_DATA_KEYS = ["url", "http.query", "http.url"];

const SENTRY_RUNTIME_CONTEXT_KEYS = new Set([
  "browser",
  "os",
  "device",
  "runtime",
  "app",
]);

const scrubRuntimeContextValue = (value) => {
  if (typeof value === "string") return scrubString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  return scrubSentryValue(value);
};

const scrubRuntimeContext = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return scrubSentryValue(value);
  }
  const preserved = {};
  const rest = { ...value };
  Object.keys(value).forEach((key) => {
    const normalized = normalizeKey(key);
    if (normalized !== "name" && normalized !== "version") return;
    preserved[key] = scrubRuntimeContextValue(value[key]);
    delete rest[key];
  });
  return { ...scrubSentryValue(rest), ...preserved };
};

const scrubSentryContexts = (contexts) => {
  if (!contexts || typeof contexts !== "object" || Array.isArray(contexts)) {
    return scrubSentryValue(contexts);
  }
  const output = {};
  Object.entries(contexts).forEach(([key, value]) => {
    if (SENTRY_RUNTIME_CONTEXT_KEYS.has(normalizeKey(key))) {
      output[key] = scrubRuntimeContext(value);
      return;
    }
    output[key] = scrubSentryValue(value);
  });
  return output;
};

const scrubSpan = (span) => {
  if (!span || typeof span !== "object") return span;
  const next = { ...span };
  if (typeof next.description === "string") {
    next.description = scrubString(next.description);
  }
  if (next.data && typeof next.data === "object") {
    const data = { ...next.data };
    SPAN_URL_DATA_KEYS.forEach((key) => {
      if (typeof data[key] === "string") data[key] = scrubString(data[key]);
    });
    next.data = scrubSentryValue(data);
  }
  return next;
};

const fallbackSentryEvent = (event) => {
  try {
    const message =
      typeof event?.message === "string"
        ? scrubString(event.message)
        : "sentry scrub failed";
    const level = typeof event?.level === "string" ? event.level : "error";
    const tags = {};
    if (event?.tags && typeof event.tags === "object") {
      Object.entries(event.tags).forEach(([key, value]) => {
        if (typeof value === "string") tags[key] = scrubString(value);
        else if (typeof value === "number" || typeof value === "boolean") {
          tags[key] = value;
        }
      });
    }
    return { message, tags, level };
  } catch {
    return { message: "sentry scrub failed", tags: {}, level: "error" };
  }
};

const scrubSentryEventUnsafe = (event) => {
  if (!event || typeof event !== "object") return event;
  const next = { ...event };

  if (next.user) {
    const user = scrubSentryUser(next.user);
    if (user) next.user = user;
    else delete next.user;
  }

  if (next.request && typeof next.request === "object") {
    const request = { ...next.request };
    const userAgent = readUserAgent(request.headers);
    delete request.headers;
    delete request.cookies;
    if ("data" in request) request.data = describeValueShape(request.data);
    if (typeof request.url === "string") request.url = scrubString(request.url);
    if (typeof request.query_string === "string") {
      request.query_string = scrubString(request.query_string);
    }
    next.request = scrubSentryValue(request);
    if (userAgent) next.request.headers = { "User-Agent": userAgent };
  }

  if (typeof next.transaction === "string") {
    next.transaction = scrubString(next.transaction);
  }
  if (Array.isArray(next.spans)) {
    next.spans = next.spans.map((span) => scrubSpan(span));
  }

  if (next.extra) next.extra = scrubSentryValue(next.extra);
  if (next.contexts) next.contexts = scrubSentryContexts(next.contexts);
  if (next.tags) next.tags = scrubSentryValue(next.tags);
  if (typeof next.message === "string")
    next.message = scrubString(next.message);
  if (next.exception) next.exception = scrubSentryValue(next.exception);
  if (next.logentry) next.logentry = scrubSentryValue(next.logentry);
  if (Array.isArray(next.breadcrumbs)) {
    next.breadcrumbs = next.breadcrumbs
      .map((breadcrumb) => scrubSentryBreadcrumb(breadcrumb))
      .filter(Boolean);
  }

  return next;
};

export const scrubSentryBreadcrumb = (breadcrumb) => {
  try {
    if (!breadcrumb || typeof breadcrumb !== "object") return breadcrumb;
    return scrubSentryValue(breadcrumb);
  } catch {
    return null;
  }
};

export const scrubSentryEvent = (event) => {
  try {
    return scrubSentryEventUnsafe(event);
  } catch {
    return fallbackSentryEvent(event);
  }
};

export const initSentry = ({
  sentryDsn = import.meta.env?.VITE_SENTRY_URL,
  isStaging = import.meta.env?.VITE_IS_STAGING,
  isDev = import.meta.env?.DEV,
} = {}) => {
  const decision = resolveSentryEnabled({ sentryDsn, isStaging, isDev });

  if (decision.misconfigured) {
    console.error(
      "Sentry DSN is missing in production; errors will not be delivered."
    );
  }

  if (!decision.enabled) return decision;

  const client = getSentry();
  client.init({
    dsn: decision.dsn,
    environment: decision.environment,
    sendDefaultPii: false,
    tracesSampleRate: 0.2,
    beforeSend(event) {
      return scrubSentryEvent(event);
    },
    beforeSendTransaction(event) {
      return scrubSentryEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      return scrubSentryBreadcrumb(breadcrumb);
    },
    integrations: (defaults) => {
      const list = (Array.isArray(defaults) ? defaults : []).filter(
        (integration) => integration?.name !== "GlobalHandlers"
      );
      const hasTracing = list.some(
        (integration) => integration?.name === "BrowserTracing"
      );
      if (
        !hasTracing &&
        typeof client.browserTracingIntegration === "function"
      ) {
        list.push(client.browserTracingIntegration());
      }
      return list;
    },
  });

  return decision;
};

export const isExpectedUserError = ({
  code,
  message,
  name,
  cancelled = false,
} = {}) => {
  if (cancelled === true && code !== "jobTimeout") return true;
  if (name === "AbortError") return true;
  if (typeof code === "string") {
    if (EXPECTED_USER_ERROR_CODES.has(code)) return true;
    if (code.endsWith("Valid")) return true;
    if (code.startsWith("imageResizingCustomDimensions")) return true;
  }
  if (typeof message === "string") {
    const trimmed = message.trim();
    if (CANCELLED_MESSAGES.some((pattern) => pattern.test(trimmed))) {
      return true;
    }
    if (/resizeobserver loop/i.test(trimmed)) return true;
    if (
      trimmed === "Permission denied" ||
      trimmed === "PERMISSION_DENIED: Permission denied"
    ) {
      return true;
    }
  }
  return false;
};

export const shouldReportErrorToSentry = ({
  code,
  message,
  name,
  cancelled = false,
  skipSentry = false,
  isNetwork = false,
  reportPermissionDenied = false,
  reportUnreachableBackend = false,
} = {}) => {
  if (skipSentry === true) return false;
  if (isNetwork === true || code === "ERR_NETWORK") {
    return reportUnreachableBackend === true;
  }
  if (
    reportPermissionDenied === true &&
    isRtdbPermissionDenied({ code, message })
  ) {
    return true;
  }
  return !isExpectedUserError({ code, message, name, cancelled });
};

export const sentryMessageFromError = ({
  message,
  code,
  fallback = "Unknown error",
} = {}) => {
  if (typeof message === "string" && message.trim()) return message.trim();
  if (code !== undefined && code !== null && String(code).trim()) {
    return String(code);
  }
  return fallback;
};

export const logErrorToSentry = ({
  errorFormatted,
  rawError,
  userID,
  functionName,
  context = {},
  tags = {},
  level = "error",
  capture = "message",
} = {}) => {
  try {
    const message = scrubString(
      sentryMessageFromError({
        message: errorFormatted?.message,
        code: errorFormatted?.code,
      })
    );
    let eventName = functionName ? `Error on - ${functionName}` : message;

    const isErrorCampaign = errorFormatted?.code === "errorProcessingCampaigns";
    if (isErrorCampaign) {
      eventName = `Error on - ${functionName} ${context?.campaignID ? `${context.campaignID}` : ""}`;
    }
    eventName = scrubString(eventName);

    const safeTags = scrubSentryValue(tags || {});
    const safeContext = scrubSentryValue({
      functionName: functionName || null,
      userID: userID || null,
      ...(context && typeof context === "object" ? context : {}),
    });

    const client = getSentry();
    client.withScope((scope) => {
      scope.setLevel(level);
      if (typeof scope.setUser === "function" && userID) {
        scope.setUser({ id: String(userID) });
      }
      scope.setTag("source", safeTags?.source || "LaunchError");
      if (functionName)
        scope.setTag("function", scrubString(String(functionName)));
      if (errorFormatted?.code) {
        scope.setTag("code", scrubString(String(errorFormatted.code)));
      }
      if (userID) scope.setTag("userID", String(userID));
      Object.entries(safeTags || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (isSensitiveKey(key)) return;
        const tagValue =
          typeof value === "string" ? value : scrubString(String(value));
        scope.setTag(key, tagValue);
      });

      scope.setContext(
        "error",
        scrubSentryValue({
          message: rawError?.message || message,
          code: errorFormatted?.code || rawError?.code || "generalError",
        })
      );

      scope.setContext("context", safeContext);

      const platformErrors = errorFormatted?.data?.errors;
      if (platformErrors && typeof platformErrors === "object") {
        const errorsMessagesPlatform = {};
        Object.keys(platformErrors).forEach((platform) => {
          errorsMessagesPlatform[platform] =
            `${platformErrors?.[platform]?.code || "generalError"}: ${
              platformErrors?.[platform]?.message || "No message"
            }`;
        });
        scope.setContext(
          "platformErrors",
          scrubSentryValue({
            errors: errorsMessagesPlatform,
            affectedPlatforms: Object.keys(platformErrors),
          })
        );
      }

      if (capture === "exception" && rawError instanceof Error) {
        client.captureException(sanitizeErrorForCapture(rawError));
        return;
      }

      client.captureMessage(eventName);
    });
  } catch {
    // Never let the reporter break the user flow.
  }
};
